import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { PageThumb } from './PageThumb';
import type { OpenFile, PageItem } from './use-pdf-workspace';

const LONG_PRESS_MS = 450;
const DRAG_THRESHOLD_PX = 6;

interface Gesture {
  readonly index: number;
  readonly key: string;
  readonly pointerType: string;
  readonly x: number;
  readonly y: number;
  readonly withShift: boolean;
  readonly withToggle: boolean;
  dragging: boolean;
  timer: number | null;
}

function newGesture(event: PointerEvent<HTMLButtonElement>, index: number, key: string): Gesture {
  return {
    index,
    key,
    pointerType: event.pointerType,
    x: event.clientX,
    y: event.clientY,
    withShift: event.shiftKey,
    withToggle: event.metaKey || event.ctrlKey,
    dragging: false,
    timer: null,
  };
}

function selectHow(g: Gesture): 'only' | 'toggle' | 'range' {
  if (g.pointerType === 'touch' || g.withToggle) return 'toggle';
  return g.withShift ? 'range' : 'only';
}

const indexAt = (x: number, y: number): number | null => {
  const el = document.elementFromPoint(x, y)?.closest<HTMLElement>('[data-page-index]');
  return el ? Number(el.dataset.pageIndex) : null;
};

/** The pointer gestures over the pages: click or tap selects, a drag (long press on touch) moves. */
function usePageGestures(
  onSelect: (key: string, how: 'only' | 'toggle' | 'range') => void,
  onMove: (from: number, to: number) => void,
) {
  const gesture = useRef<Gesture | null>(null);
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [dropAt, setDropAt] = useState<number | null>(null);

  // While a touch drag runs, the page must not scroll under the finger.
  useEffect(() => {
    if (dragFrom === null) return;
    const block = (event: TouchEvent) => event.preventDefault();
    document.addEventListener('touchmove', block, { passive: false });
    return () => document.removeEventListener('touchmove', block);
  }, [dragFrom]);

  const startDrag = (g: Gesture): void => {
    g.dragging = true;
    setDragFrom(g.index);
    setDropAt(g.index);
  };

  const onPointerDown = (
    event: PointerEvent<HTMLButtonElement>,
    index: number,
    key: string,
  ): void => {
    if (event.button !== 0) return;
    const g = newGesture(event, index, key);
    if (event.pointerType === 'touch') {
      g.timer = window.setTimeout(() => startDrag(g), LONG_PRESS_MS);
    }
    gesture.current = g;
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const onPointerMove = (event: PointerEvent<HTMLButtonElement>): void => {
    const g = gesture.current;
    if (!g) return;
    const moved = Math.hypot(event.clientX - g.x, event.clientY - g.y) > DRAG_THRESHOLD_PX;
    if (!g.dragging) {
      if (g.pointerType === 'touch') {
        // A touch that moves before the long press is a scroll, not a drag.
        if (moved && g.timer !== null) {
          window.clearTimeout(g.timer);
          gesture.current = null;
        }
        return;
      }
      if (moved) startDrag(g);
      else return;
    }
    const at = indexAt(event.clientX, event.clientY);
    if (at !== null) setDropAt(at);
  };

  const finish = (cancelled: boolean): void => {
    const g = gesture.current;
    gesture.current = null;
    if (!g) return;
    if (g.timer !== null) window.clearTimeout(g.timer);
    if (g.dragging) {
      // The bar shows «before the page under the finger»: moving forward, that is one index less
      // once the page has left its old place.
      if (!cancelled && dropAt !== null) onMove(g.index, dropAt > g.index ? dropAt - 1 : dropAt);
    } else if (!cancelled) {
      onSelect(g.key, selectHow(g));
    }
    setDragFrom(null);
    setDropAt(null);
  };

  return { dragFrom, dropAt, onPointerDown, onPointerMove, finish };
}

interface CellHandlers {
  readonly onPointerDown: (
    event: PointerEvent<HTMLButtonElement>,
    index: number,
    key: string,
  ) => void;
  readonly onPointerMove: (event: PointerEvent<HTMLButtonElement>) => void;
  readonly finish: (cancelled: boolean) => void;
  readonly onKeyDown: (event: KeyboardEvent<HTMLButtonElement>, index: number, key: string) => void;
  readonly register: (el: HTMLButtonElement | null) => void;
}

function PageCell(p: {
  readonly item: PageItem;
  readonly index: number;
  readonly file: OpenFile;
  readonly selected: boolean;
  readonly dragFrom: number | null;
  readonly dropAt: number | null;
  readonly handlers: CellHandlers;
}) {
  const { t } = useTranslation('tools');
  const { item, index, file, selected, dragFrom, dropAt, handlers } = p;
  return (
    <li data-page-index={index} className="relative min-w-0">
      {dropAt === index && dragFrom !== null && dragFrom !== index && (
        <span aria-hidden="true" className="absolute top-0 -left-1.5 h-full w-1 rounded bg-ring" />
      )}
      <button
        ref={handlers.register}
        type="button"
        aria-pressed={selected}
        aria-label={t('pdf.grid.page', { number: index + 1 })}
        onPointerDown={(e) => handlers.onPointerDown(e, index, item.key)}
        onPointerMove={handlers.onPointerMove}
        onPointerUp={() => handlers.finish(false)}
        onPointerCancel={() => handlers.finish(true)}
        onKeyDown={(e) => handlers.onKeyDown(e, index, item.key)}
        onContextMenu={(e) => e.preventDefault()}
        className={cn(
          'flex w-full touch-manipulation flex-col items-center gap-1.5 rounded-md p-0 select-none',
          'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none',
          dragFrom === null ? 'cursor-grab' : 'cursor-grabbing',
        )}
      >
        <PageThumb
          thumbs={file.thumbs}
          page={item.page}
          rotate={item.rotate}
          selected={selected}
          dragging={dragFrom === index}
          images={item.images.length}
          imagesLabel={t('pdf.grid.images', { count: item.images.length })}
        />
        <span className="text-xs font-medium tabular-nums text-muted-foreground">{index + 1}</span>
      </button>
    </li>
  );
}

/**
 * The pages of every open file as one grid (#387 «PDF» boards). Click selects one page, Shift a
 * range, ⌘ or Ctrl adds or removes one; on a touch screen a tap adds or removes. Drag moves a page
 * (long press on a touch screen). With the keyboard: Space or Enter selects, Alt with the arrows
 * moves the focused page.
 */
export function PageGrid({
  pages,
  files,
  selected,
  onSelect,
  onMove,
}: {
  readonly pages: readonly PageItem[];
  readonly files: readonly OpenFile[];
  readonly selected: ReadonlySet<string>;
  readonly onSelect: (key: string, how: 'only' | 'toggle' | 'range') => void;
  readonly onMove: (from: number, to: number) => void;
}) {
  const { t } = useTranslation('tools');
  const { dragFrom, dropAt, onPointerDown, onPointerMove, finish } = usePageGestures(
    onSelect,
    onMove,
  );
  const buttons = useRef(new Map<string, HTMLButtonElement>());

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number, key: string): void => {
    if (event.altKey && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
      event.preventDefault();
      const to = index + (event.key === 'ArrowLeft' ? -1 : 1);
      if (to < 0 || to >= pages.length) return;
      onMove(index, to);
      requestAnimationFrame(() => buttons.current.get(key)?.focus());
      return;
    }
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      onSelect(key, 'toggle');
    }
  };

  return (
    <ol
      aria-label={t('pdf.grid.label')}
      className="grid grid-cols-3 gap-x-2 gap-y-3.5 sm:grid-cols-[repeat(auto-fill,minmax(104px,1fr))] sm:gap-x-3 sm:gap-y-4"
    >
      {pages.map((item, index) => {
        const file = files.find((f) => f.id === item.source);
        if (!file) return null;
        return (
          <PageCell
            key={item.key}
            item={item}
            index={index}
            file={file}
            selected={selected.has(item.key)}
            dragFrom={dragFrom}
            dropAt={dropAt}
            handlers={{
              onPointerDown,
              onPointerMove,
              finish,
              onKeyDown,
              register: (el) => {
                if (el) buttons.current.set(item.key, el);
                else buttons.current.delete(item.key);
              },
            }}
          />
        );
      })}
    </ol>
  );
}
